import { DirectionalHint } from "@fluentui/react/lib/Callout";

import type {
  AppControlBaseStoreOnlyProps,
  AppSharedTooltipProps,
  SetTooltipProps,
} from "@1js/acui-common";
import type {
  MenuDefinition,
  MenuSection,
  SimpleMenuItemControlProps,
} from "@1js/acui-menu";
import { AppTooltipHost } from "@1js/acui-tooltip";

import { isAppFlyoutAnchorProps } from "../AppFlyoutAnchor";
import { isAppSplitButtonProps } from "../AppSplitButton";
import { doesControlSupportTooltipsInMenus } from "./doesControlSupportTooltipsInMenus";

function getLabelForTooltip(
  appControlProps: SimpleMenuItemControlProps
): string {
  switch (appControlProps.type) {
    case "AppSpinButtonProps":
    case "AppComboBoxProps":
    case "AppCheckboxProps":
    case "AppFlyoutAnchorProps":
    case "AppSplitButtonProps":
    case "AppToggleButtonProps":
    case "AppCompoundButtonProps":
    case "AppButtonProps":
      return (
        (appControlProps as AppControlBaseStoreOnlyProps).customTooltip || ""
      );
    default: {
      return "";
    }
  }
}

export function findInnerButtonForHover(
  htmlParent: HTMLElement,
  menuItemControlProps: SimpleMenuItemControlProps
): HTMLElement {
  switch (menuItemControlProps.type) {
    case "AppSpinButtonProps":
    case "AppCheckboxProps": {
      return htmlParent.querySelector("div") as HTMLElement;
    }
    case "AppComboBoxProps": {
      const parentDiv = htmlParent.querySelector("div") as HTMLElement;
      return parentDiv.children && (parentDiv.children[0] as HTMLElement);
    }
    default: {
      return htmlParent.querySelector("button") as HTMLElement;
    }
  }
}

// Creates and returns an array of SimpleMenuItemControlProps corresponding to every element in the
// provided MenuDefinition. This will look through and add all items from each menu section.
export function createArrayOfSimpleMenuItemControlProps(
  menuDefinition?: MenuDefinition
): SimpleMenuItemControlProps[] {
  const menuSections = menuDefinition ? menuDefinition.sections : undefined;
  if (menuSections === undefined) {
    return [];
  }

  return menuSections
    .concat(menuDefinition?.footer ? menuDefinition.footer : [])
    .reduce(
      (
        controls: SimpleMenuItemControlProps[],
        section: MenuSection
      ): SimpleMenuItemControlProps[] => {
        return controls.concat(
          section.controls.filter((x) => doesControlSupportTooltipsInMenus(x))
        );
      },
      []
    );
}

// Creates and returns an array of HTML list items in the dropdown menu. The
// flyoutId corresponds to the ID on the flyout that holds the list items. We
// only add items that are buttons or input elements to the array and skip things
// like menu section headers and titles.
export function createArrayOfHTMLItems(
  flyoutId?: string,
  targetWindow?: Window
): HTMLElement[] {
  if (flyoutId === undefined) {
    return [];
  }
  const currentWindow = targetWindow || window;
  const menu = currentWindow.document.getElementById(flyoutId);
  if (menu === null) {
    return [];
  }

  const listItems = menu.querySelectorAll("li");
  const htmlItems: HTMLElement[] = [];

  // For each of the list items, we want to check if the child node is either a button
  // or an input, which should be the only elements that need a tooltip
  // Other elements in the menu are likely either nested list elements, plain text
  // elements such as the menu title / menu section headers, or menu dividers.
  // One exception: some buttons (like CompoundButtons) have the Button element
  // wrapped in a DIV, so in that case we want to check one more level down.
  listItems.forEach((item: HTMLElement) => {
    if (item && item.hasChildNodes()) {
      if (item.children[0].nodeName === "INPUT") {
        htmlItems.push(item);
      } else {
        let possibleParentOfButton: HTMLElement = item;

        if (item.children[0].nodeName === "DIV") {
          if (
            !item.firstChild!.hasChildNodes &&
            !item.firstChild!.hasChildNodes()
          ) {
            return;
          }

          possibleParentOfButton = item.firstChild as HTMLElement;
        }

        if (possibleParentOfButton.children[0].nodeName === "BUTTON") {
          htmlItems.push(possibleParentOfButton);
        }
      }
    }
  });

  return htmlItems;
}

// Returns the AppControlProps corresponding to the anchor element
// that hosts the menu. The flyoutAnchorId is used to identify the
// anchor element id that we are looking for.
export function findAnchorButtonProps(
  flyoutAnchorId: string,
  itemList: SimpleMenuItemControlProps[],
  recursiveIt?: boolean
): SimpleMenuItemControlProps | undefined {
  if (!itemList || itemList.length === 0) {
    return undefined;
  }

  const items: SimpleMenuItemControlProps[] = [];
  for (let index = 0; index < itemList.length; index = index + 1) {
    const buttonProp = itemList[index];
    if (buttonProp.id === flyoutAnchorId) {
      return buttonProp;
    }
    if (!recursiveIt) {
      items.push(buttonProp);
    } else if (
      isAppFlyoutAnchorProps(buttonProp) ||
      isAppSplitButtonProps(buttonProp)
    ) {
      return findAnchorButtonProps(
        flyoutAnchorId,
        createArrayOfSimpleMenuItemControlProps(buttonProp.menuDefinition),
        true
      );
    }
  }

  return findAnchorButtonProps(flyoutAnchorId, items, true);
}

export function setTooltipsOnMenuItems(
  menuDefinition: MenuDefinition | undefined,
  menuId: string,
  map: Map<string, AppTooltipHost>,
  setTooltipElement?: (tooltipProps: SetTooltipProps) => void,
  updateTooltipsHidden?: (hideAllTooltips: boolean) => void,
  performSetOnNextFrame?: boolean,
  targetWindow?: Window
): Map<string, AppTooltipHost> {
  if (menuDefinition && setTooltipElement && updateTooltipsHidden) {
    // This gets two similar arrays of items, one with the HTMLElements and one with their
    // corresponding AppProps. We then loop through the two arrays concurrently so we can register
    // each item with the AppTooltipHost.
    const htmlItems = createArrayOfHTMLItems(menuId, targetWindow);
    const buttonProps = createArrayOfSimpleMenuItemControlProps(menuDefinition);
    const tooltipHostItems: Map<string, AppTooltipHost> = new Map();

    if (htmlItems && buttonProps && htmlItems.length === buttonProps.length) {
      buttonProps.forEach(
        (
          buttonProp: SimpleMenuItemControlProps & AppSharedTooltipProps,
          index: number
        ) => {
          const label = getLabelForTooltip(buttonProp);

          if (label !== "") {
            const element = htmlItems[index];
            const hoverElement = findInnerButtonForHover(element, buttonProp);

            const tooltipHost = new AppTooltipHost({
              updateTooltipsHidden,
              setTooltipElement,
              htmlElement: element,
              controlId: buttonProp.id,
              controlLabel: label,
              tooltipTopMargin: 0,
              isMenuComponent: true,
              hoverElement,
              tooltipDirection:
                buttonProp.tooltipDirection || DirectionalHint.leftCenter,
              performSetOnNextFrame: !!performSetOnNextFrame,
            });
            tooltipHost.registerTooltipHostActions();
            tooltipHostItems.set(buttonProp.id, tooltipHost);
          }
        }
      );
    }

    tooltipHostItems.forEach((value: AppTooltipHost, key: string) => {
      map.set(key, value);
    });
  }

  return map;
}
